import type { KVStore } from './storage';
import type { AnalyticsEvent } from './types';

const QUEUE_KEY = 'narrato_queue';

/**
 * FIFO event buffer persisted to storage, so unsent events survive page
 * loads and offline periods. Bounded: beyond maxSize the oldest events
 * are dropped (freshest data wins for analytics).
 *
 * Storage is the source of truth: every operation reads, changes and
 * writes it back, so tabs of the same site share one queue instead of
 * overwriting each other's unsent events.
 */
export class EventQueue {
  /** Set once storage refuses a write (full): the queue lives in memory
   * for the rest of this page's life rather than losing events. The last
   * stored copy stays, so the next page load resends what it holds (a
   * duplicate at worst, never a loss). */
  private memory: AnalyticsEvent[] | null = null;

  constructor(
    private store: KVStore,
    private maxSize: number,
  ) {}

  get size(): number {
    return this.load().length;
  }

  /** Append events (one read, one write); returns the new size. */
  push(...added: AnalyticsEvent[]): number {
    const events = this.load().concat(added).slice(-this.maxSize);
    this.save(events);
    return events.length;
  }

  peek(count: number): AnalyticsEvent[] {
    return this.load().slice(0, count);
  }

  /** Remove these events (after a send), by id: the queue may have
   * changed while the request was in flight. */
  remove(eventIds: ReadonlySet<string>): void {
    this.save(this.load().filter((e) => !eventIds.has(e.event_id)));
  }

  /** Take every event except `keep` (events already being sent) out of the
   * queue and return them. */
  drainExcept(keep: ReadonlySet<string>): AnalyticsEvent[] {
    const events = this.load();
    this.save(events.filter((e) => keep.has(e.event_id)));
    return events.filter((e) => !keep.has(e.event_id));
  }

  private load(): AnalyticsEvent[] {
    if (this.memory) return [...this.memory];
    const raw = this.store.get(QUEUE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as AnalyticsEvent[]) : [];
    } catch {
      return []; // corrupted queue: start empty
    }
  }

  private save(events: AnalyticsEvent[]): void {
    if (!this.memory && this.store.set(QUEUE_KEY, JSON.stringify(events))) return;
    this.memory = events;
  }
}
